import { useMemo } from 'react';

import { useCategoryExternalBanners } from './use-category-external-banners.hook';

import type { OffersCarouselCategory } from './offers-carousel-category.types';

export type UseCategoryContentResult = {
  category: OffersCarouselCategory | null;
  isLoading: boolean;
  refetch?: () => Promise<unknown>;
};

export type UseCategoryContentParams = {
  category: OffersCarouselCategory | null;
  refetchEager?: () => Promise<unknown>; // Eager batch refetch from carousel prefetch. Used when selected category is not lazy
};

export const useCategoryContent = (params: UseCategoryContentParams): UseCategoryContentResult => {
  const { category, refetchEager } = params;
  const isLazy = !!category?.shouldLazyLoadBanners;

  const {
    banners: lazyBanners,
    loading: lazyLoading,
    refetch: refetchLazy,
  } = useCategoryExternalBanners({
    bannerIds: category?.bannerIds,
    skip: !isLazy,
  });

  const resolvedCategory = useMemo(() => {
    if (!category) {
      return null;
    }
    if (!isLazy) {
      return category;
    }
    return {
      ...category,
      banners: lazyBanners,
    };
  }, [category, isLazy, lazyBanners]);

  return {
    category: resolvedCategory,
    isLoading: isLazy && lazyLoading,
    // eslint-disable-next-line no-nested-ternary
    refetch: !category ? undefined : isLazy ? refetchLazy : refetchEager,
  };
};
